import React, { useRef, useState, useEffect } from "react";
import api from "../../../utils/api";

export type BackgroundImageData = {
	previewUrl: string;
	publicUrl?: string;
	objectName?: string;
	uploading?: boolean;
	error?: string;
};

interface BackgroundImageUploadProps {
	value: BackgroundImageData | null;
	onChange: (value: BackgroundImageData | null) => void;
}

const BackgroundImageUpload: React.FC<BackgroundImageUploadProps> = ({
	value,
	onChange,
}) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [localObjectUrl, setLocalObjectUrl] = useState<string | null>(null);

	useEffect(() => {
		return () => {
			if (localObjectUrl) {
				URL.revokeObjectURL(localObjectUrl);
			}
		};
	}, [localObjectUrl]);

	const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragging(false);

		if (event.dataTransfer.files && event.dataTransfer.files[0]) {
			uploadBackground(event.dataTransfer.files[0]);
		}
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files[0]) {
			uploadBackground(event.target.files[0]);
		}
	};

	const uploadBackground = async (file: File) => {
		if (!file.type.startsWith("image/")) {
			onChange({
				previewUrl: value?.previewUrl || "",
				error: "画像ファイルを選択してください",
			});
			return;
		}

		const objectUrl = URL.createObjectURL(file);
		setLocalObjectUrl(objectUrl);
		onChange({
			previewUrl: objectUrl,
			uploading: true,
			error: undefined,
		});

		try {
			const uploaded = await uploadToGCS(file);
			onChange({
				previewUrl: uploaded.publicUrl,
				publicUrl: uploaded.publicUrl,
				objectName: uploaded.objectName,
				uploading: false,
			});
		} catch (error) {
			const message =
				error instanceof Error
					? error.message
					: "背景画像のアップロードに失敗しました";
			onChange(
				value
					? {
						...value,
						uploading: false,
						error: message,
					}
					: {
						previewUrl: "",
						uploading: false,
						error: message,
					}
			);
		} finally {
			URL.revokeObjectURL(objectUrl);
			setLocalObjectUrl(null);
		}
	};

	const uploadToGCS = async (
		file: File
	): Promise<{ publicUrl: string; objectName?: string }> => {
		const signedUrlResponse: any = await api.post("/upload/signed-url", {
			filename: file.name,
			contentType: file.type,
		});

		const signedData =
			"data" in signedUrlResponse ? signedUrlResponse.data : signedUrlResponse;

		const { signedUrl, publicUrl, objectName } = signedData;

		const uploadResponse = await fetch(signedUrl, {
			method: "PUT",
			headers: {
				"Content-Type": file.type,
			},
			body: file,
		});

		if (!uploadResponse.ok) {
			throw new Error("GCSへのアップロードに失敗しました");
		}

		const confirmResponse: any = await api.post("/upload/confirm", {
			publicUrl: encodeURI(publicUrl),
			objectName,
		});

		const confirmData =
			"data" in confirmResponse ? confirmResponse.data : confirmResponse;

		let finalUrl = confirmData?.url || confirmData?.publicUrl || publicUrl;

		if (!finalUrl && typeof confirmData?.message === "string") {
			const match = confirmData.message.match(/https?:\/\/[^\s"]+/);
			if (match) {
				finalUrl = match[0];
			}
		}

		if (!finalUrl) {
			throw new Error("アップロード済みURLを取得できませんでした");
		}

		return { publicUrl: finalUrl, objectName };
	};

	const handleRemove = () => {
		onChange(null);
	};

	return (
		<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
			<h3 className="text-lg mb-2 font-semibold text-gray-900 dark:text-white">
				背景画像（任意）
			</h3>
			<p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
				ヘッダーの背景としてぼかして表示されます。推奨サイズ 1920×1080px。
				未設定の場合はメイン画像を使用します。
			</p>
			<input
				type="file"
				accept="image/*"
				className="hidden"
				ref={fileInputRef}
				onChange={handleFileChange}
				aria-label="背景画像を選択"
			/>
			<div
				className={`w-full p-6 mb-4 border-2 border-dashed rounded-lg text-center transition-colors duration-200 relative overflow-hidden ${
					value
						? "border-transparent text-white"
						: isDragging
						? "border-violet-500 bg-violet-50 text-violet-600 dark:text-violet-200 dark:bg-violet-900/20"
						: "border-violet-300 text-violet-500 hover:bg-violet-50 dark:border-violet-600 dark:text-violet-400 dark:hover:bg-violet-900/20"
				}`}
				onDragOver={(e) => {
					e.preventDefault();
					setIsDragging(true);
				}}
				onDragLeave={(e) => {
					e.preventDefault();
					setIsDragging(false);
				}}
				onDrop={handleDrop}
				style={
					value?.previewUrl
						? {
							backgroundImage: `linear-gradient(rgba(15,23,42,0.7), rgba(15,23,42,0.7)), url(${value.previewUrl})`,
							backgroundSize: "cover",
							backgroundPosition: "center",
						}
						: undefined
				}
			>
				{value?.previewUrl && (
					<div className="absolute inset-0 bg-slate-900/60" aria-hidden="true" />
				)}
				<div className="relative z-10 space-y-2">
					{value && value.uploading ? (
						<div className="flex flex-col items-center text-white">
							<svg
								className="animate-spin h-10 w-10 text-white"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
							>
								<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
								<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
							</svg>
							<p className="mt-2 text-sm">アップロード中...</p>
						</div>
					) : (
						<>
							<svg
								className={`mx-auto h-12 w-12 ${
									value ? "text-white/80" : "text-violet-300 dark:text-violet-600"
								}`}
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
								></path>
							</svg>
							<p
								className={`text-base font-medium ${
									value
										? "text-white"
										: "text-gray-700 dark:text-gray-300"
								}`}
							>
								{value
									? "背景画像はドラッグ＆ドロップで差し替えられます"
									: "背景画像をドラッグ＆ドロップ"}
							</p>
							<p className={`text-sm ${value ? "text-white/80" : "text-gray-600 dark:text-gray-400"}`}>
								または
							</p>
							<div className="flex flex-wrap justify-center gap-3">
								<button
									type="button"
									onClick={() => fileInputRef.current?.click()}
									className="px-5 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg hover:from-violet-600 hover:to-purple-700 transition-all text-sm font-semibold"
								>
									ファイルを選択
								</button>
								{value && (
									<button
										type="button"
										onClick={handleRemove}
										className="px-5 py-2 bg-white/90 text-violet-600 rounded-lg text-sm font-semibold hover:bg-white"
									>
										削除
									</button>
								)}
							</div>
							<p
								className={`text-xs ${
									value ? "text-white/80" : "text-gray-500 dark:text-gray-400"
								}`}
							>
								1枚のみ設定できます（JPEG / PNG / GIF）
							</p>
						</>
					)}
				</div>
			</div>

			{value?.error && !value.uploading && (
				<p className="text-sm text-red-500 dark:text-red-400 mt-2">{value.error}</p>
			)}
		</div>
	);
};

export default BackgroundImageUpload;
